/**
 * Display adapter for already-rendered React children, used by the markdown
 * fallback table where the cell arrives as elements and there is no source
 * string to segment.
 *
 * CLOSED BY CONSTRUCTION. A node is typeset only when its type is recognized.
 * "Walk the tree and typeset every string leaf" was rejected as unsafe: an
 * unknown component's children may be a portal, a memoized subtree, or the
 * internals of a protocol component, and typesetting inside one corrupts
 * something this layer cannot see. Unrecognized types are preserved whole and
 * reported, so a gap shows up as a message rather than as wrong output.
 *
 * String leaves delegate to `EditorialInlineText` rather than repeating its
 * work: a table swaps renderers mid-stream, and two independently-maintained
 * segmentation passes would agree on the corpus and diverge on the first case
 * nobody wrote down. One engine, two entry points — they cannot disagree.
 *
 * Tag policy comes from `classifyEditorialCapabilities`, never a second tag
 * list. Component policy is identity-based, which is a different question the
 * classifier cannot answer: it reads tag names, and a component has none.
 */
import {
    Children,
    Fragment,
    cloneElement,
    isValidElement,
    type ReactElement,
    type ReactNode,
} from "react";

import { EditorialGlyph } from "./editorial-glyph";
import { EditorialInlineText } from "./editorial-inline-text";
import {
    hasVisibleDisplay,
    scanEditorialText,
} from "./utils/emoji-typesetting";
import {
    classifyEditorialCapabilities,
    isEditorialTouchable,
} from "./utils/editorial-capabilities";
import type { EditorialGlyphUsage } from "./utils/editorial-policy";

const DEV_DIAGNOSTIC =
    typeof import.meta.env !== "undefined" && import.meta.env.DEV === true;

/**
 * Which of the host app's components this walk may act on.
 *
 * The capability classifier answers questions about TAG NAMES, and in a
 * react-markdown tree almost nothing is a tag: `a`, `code`, `sup` and `span`
 * are all replaced by component overrides, so a policy that only classified
 * tags would meet function components and report every one of them. Component
 * identity is a different question, and it belongs to whoever owns the
 * override map — which is the wiring site, not this module.
 */
export interface EditorialComponentPolicy {
    /** Preserved whole and silently: known not to be ours to touch. */
    readonly opaque?: ReadonlySet<unknown>;
    /**
     * Recursed into, but rolled back if typesetting would empty the label.
     * For components that render their children as a user-facing label and
     * nothing else — an anchor override being the case that matters.
     */
    readonly transactional?: ReadonlySet<unknown>;
}

export interface EditorialReactInlineProps {
    readonly children: ReactNode;
    readonly usage?: EditorialGlyphUsage;
    readonly components?: EditorialComponentPolicy;
}

/** What an unrecognized component is called, for the diagnostic only. */
const nameOf = (type: unknown): string => {
    if (typeof type === "string") return type;
    if (typeof type === "function") {
        return (
            (type as { displayName?: string; name?: string }).displayName ??
            (type as { name?: string }).name ??
            "anonymous component"
        );
    }
    return "unknown element";
};

const reported = new WeakSet<object>();

const report = (type: unknown): void => {
    if (!DEV_DIAGNOSTIC) return;
    // Once per component type per session: this runs on every render of every
    // fallback cell, and a per-render message would bury the first one.
    if (typeof type === "object" || typeof type === "function") {
        if (reported.has(type as object)) return;
        reported.add(type as object);
    }
    console.warn(
        `[editorial] preserved <${nameOf(type)}> untouched: not on the inline ` +
            `typesetting allowlist. Add it to the allowlist if its children are ` +
            `ordinary prose, or leave it here if they are not.`
    );
};

/**
 * Every string this subtree would contribute, gathered only through nodes the
 * walk is allowed to enter. Used to decide whether typesetting would strip an
 * interactive label down to nothing.
 */
const sourceTextOf = (node: ReactNode): string => {
    if (typeof node === "string") return node;
    if (Array.isArray(node)) return node.map(sourceTextOf).join("");
    if (!isValidElement(node)) return "";
    const el = node as ReactElement<{ children?: ReactNode }>;
    return sourceTextOf(el.props.children);
};

/**
 * Whether typesetting this label would leave the reader nothing to act on.
 *
 * An emptied anchor still takes focus and still navigates while announcing
 * nothing, so the transform is abandoned rather than tidied up. A mapped glyph
 * counts as content — the label becomes a mark, which is still an affordance.
 */
const wouldEmptyLabel = (children: ReactNode): boolean =>
    !hasVisibleDisplay(sourceTextOf(children));

const isTransparentNode = (node: ReactNode): boolean => {
    if (typeof node === "string") return true;
    if (!isValidElement(node)) return false;
    const el = node as ReactElement<{ children?: ReactNode }>;
    if (el.type === Fragment) return true;
    if (el.type === EditorialGlyph) return false;
    if (typeof el.type !== "string") return false;
    const { contract } = classifyEditorialCapabilities({
        type: "element",
        tagName: el.type,
        properties: el.props as Record<string, unknown>,
    });
    return isEditorialTouchable(contract) && contract !== "interactive";
};

const endsWithSemantic = (text: string): boolean => {
    const scan = scanEditorialText(text);
    if (!scan.changed) return false;
    const last = scan.segments[scan.segments.length - 1];
    return last?.kind === "emoji" && last.disposition === "SEMANTIC";
};

const typesetChildren = (
    children: ReactNode,
    usage: EditorialGlyphUsage,
    policy: EditorialComponentPolicy,
    outerFollowingText = "",
    consumeFirstLeadingSpace = false,
    outerPrecedingText = ""
): ReactNode => {
    const nodes = Children.toArray(children);
    let previousEndsWithSemantic = consumeFirstLeadingSpace;
    let precedingText = outerPrecedingText;

    return nodes.map((child, index) => {
        if (!isTransparentNode(child)) {
            previousEndsWithSemantic = false;
            precedingText = "";
            return typesetNode(child, usage, policy);
        }

        const source = sourceTextOf(child);
        const consumeLeadingSpace =
            previousEndsWithSemantic && source.startsWith(" ");
        const effectiveSource = consumeLeadingSpace ? source.slice(1) : source;
        let followingText = outerFollowingText;
        for (let next = index + 1; next < nodes.length; next++) {
            if (!isTransparentNode(nodes[next])) {
                followingText = "";
                break;
            }
            const candidate = sourceTextOf(nodes[next]);
            if (candidate !== "") {
                followingText = candidate;
                break;
            }
        }

        const result = typesetNode(child, usage, policy, {
            consumeLeadingSpace,
            precedingText,
            followingText,
        });
        if (effectiveSource !== "") {
            previousEndsWithSemantic = endsWithSemantic(effectiveSource);
            if (hasVisibleDisplay(effectiveSource)) {
                precedingText = effectiveSource;
            }
        }
        return result;
    });
};

interface NodeContext {
    readonly consumeLeadingSpace?: boolean;
    readonly precedingText?: string;
    readonly followingText?: string;
}

function typesetNode(
    node: ReactNode,
    usage: EditorialGlyphUsage,
    policy: EditorialComponentPolicy,
    context: NodeContext = {}
): ReactNode {
    if (typeof node === "string") {
        return (
            <EditorialInlineText
                text={node}
                usage={usage}
                precedingText={context.precedingText}
                followingText={context.followingText}
                consumeLeadingSpace={context.consumeLeadingSpace}
            />
        );
    }
    // Numbers, booleans, null, undefined carry no vocabulary.
    if (!isValidElement(node)) return node;

    const el = node as ReactElement<{ children?: ReactNode }>;
    const { type } = el;

    if (type === Fragment) {
        return cloneElement(
            el,
            undefined,
            typesetChildren(
                el.props.children,
                usage,
                policy,
                context.followingText,
                context.consumeLeadingSpace,
                context.precedingText
            )
        );
    }

    // Our own mark. Re-entering it would wrap a glyph in a glyph.
    if (type === EditorialGlyph) return node;

    if (typeof type !== "string") {
        // Consult the policy the wiring site handed us: in a react-markdown
        // tree almost every inline node is a component, so an unconsulted
        // policy is not a partial failure — it is the whole walk stopping at
        // the first override it meets.
        if (policy.opaque?.has(type)) return node;

        if (policy.transactional?.has(type)) {
            // Same rollback rule the interactive TAG path uses below: a label
            // consumed entirely would leave a focusable, navigable element
            // announcing nothing.
            if (wouldEmptyLabel(el.props.children)) return node;
            return cloneElement(
                el,
                undefined,
                typesetChildren(el.props.children, usage, policy)
            );
        }

        report(type);
        return node;
    }

    const { contract } = classifyEditorialCapabilities({
        type: "element",
        tagName: type,
        properties: el.props as Record<string, unknown>,
    });

    if (!isEditorialTouchable(contract)) return node;

    if (contract === "interactive" && wouldEmptyLabel(el.props.children)) {
        return node;
    }

    return cloneElement(
        el,
        undefined,
        typesetChildren(
            el.props.children,
            usage,
            policy,
            contract === "interactive" ? "" : context.followingText,
            contract === "interactive" ? false : context.consumeLeadingSpace,
            contract === "interactive" ? "" : context.precedingText
        )
    );
}

const NO_COMPONENTS: EditorialComponentPolicy = {};

export function EditorialReactInline({
    children,
    usage = "table-cell",
    components = NO_COMPONENTS,
}: EditorialReactInlineProps) {
    return <>{typesetChildren(children, usage, components)}</>;
}
